import React from 'react';
import { AppBar, Toolbar, Box, Typography, Chip, IconButton, Tooltip, useTheme, useMediaQuery, Button } from '@mui/material';
import { Menu as MenuIcon, Settings, ExitToApp, Comment, LiveTv } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Room } from 'livekit-client';
import { LiveStreamState, UIControlsState, DeviceSelectionState, ConnectionQualityInfo } from '../types';
import { BREAKPOINTS, ANIMATION_DURATION } from '../constants';
import ConnectionQualityIndicator from './ConnectionQualityIndicator';
interface HeaderProps {
  roomName: string;
  roomId: string;
  room: Room | null;
  liveStreamState: LiveStreamState;
  uiControlsState: UIControlsState;
  connectionQualityInfo: ConnectionQualityInfo;
  onToggleSettings: () => void;
  onToggleComments: () => void;
  onToggleChannelControl: () => void;
  onLeaveRoom: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  roomName,
  roomId,
  room,
  liveStreamState,
  uiControlsState,
  connectionQualityInfo,
  onToggleSettings,
  onToggleComments,
  onToggleChannelControl,
  onLeaveRoom,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider || 'rgba(255, 255, 255, 0.1)'}`,
        boxShadow: 'none',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', padding: isMobile ? '0 12px' : '0 24px' }}>
        <Box display="flex" alignItems="center" flexGrow={1}>
          {/* Studio Icon and Title */}
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '12px',
                // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              }}
            >
              <img src="/favicon.ico/favicon-32x32.png" alt="Live4.tv" style={{ width: 20, height: 20 }} />
            </Box>
            <Box>
              <Tooltip title={t('Room: {{roomName}}', { roomName: roomName || roomId || t('Live Studio') })} arrow>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: isMobile ? '1rem' : '1.25rem',
                    lineHeight: 1.2,
                  }}
                >
                  {roomName || roomId || t('Live Studio')}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          {/* Status Chips */}
          <Box display="flex" alignItems="center" gap={1} sx={{ marginLeft: isMobile ? 1 : 3 }}>
            <Tooltip title={room ? t('Connected to room') : t('Connecting to room...')} arrow>
              <Chip
                label={room ? t('Connected') : t('Connecting')}
                size="small"
                sx={{
                  backgroundColor: room ? theme.palette.success.main + '33' : theme.palette.background.paper,
                  color: room ? theme.palette.success.main : theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 24,
                  border: room ? `1px solid ${theme.palette.success.main}` : 'none',
                  '& .MuiChip-label': {
                    padding: '0 8px',
                  },
                }}
              />
            </Tooltip>

            {/* Connection Quality Indicator */}
            {room && (
              <ConnectionQualityIndicator
                connectionInfo={connectionQualityInfo}
                compact={isMobile}
              />
            )}

            {/* {liveStreamState.isLiveStreaming && (
              <Tooltip title={t('Live streaming is active')} arrow>
                <Chip
                  label={t('LIVE')}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: 24,
                    animation: `pulse ${ANIMATION_DURATION.PULSE} infinite`,
                    boxShadow: `0 2px 8px ${theme.palette.error.main}50`,
                    '& .MuiChip-label': {
                      padding: '0 8px',
                    },
                  }}
                />
              </Tooltip>
            )} */}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
          {isMobile ? (
            // Mobile: Icon-only buttons with tooltips
            <>
              <Tooltip title={t('Stream on Social Networks')} arrow>
                <IconButton
                  onClick={onToggleChannelControl}
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: uiControlsState.showChannelControl ? theme.palette.background.paper : 'transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    width: 40,
                    height: 40,
                  }}
                >
                  <LiveTv sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Room Settings')} arrow>
                <IconButton
                  onClick={onToggleSettings}
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: uiControlsState.showSettings ? theme.palette.background.paper : 'transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    width: 40,
                    height: 40,
                  }}
                >
                  <Settings sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Live Comments')} arrow>
                <IconButton
                  onClick={onToggleComments}
                  sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: uiControlsState.showComments ? theme.palette.background.paper : 'transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    width: 40,
                    height: 40,
                  }}
                >
                  <Comment sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Leave Room')} arrow>
                <IconButton
                  onClick={onLeaveRoom}
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.error.main + '20',
                      color: theme.palette.error.main,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    width: 40,
                    height: 40,
                    marginLeft: 0.5,
                  }}
                >
                  <ExitToApp sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Menu')} arrow>
                <IconButton
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    width: 40,
                    height: 40,
                  }}
                >
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            // Desktop: Buttons with icons and labels
            <>
              <Tooltip title={t('Stream on Social Networks - Configure where to stream (Facebook, YouTube, Instagram, etc.)')} arrow>
                <Button
                  onClick={onToggleChannelControl}
                  startIcon={<LiveTv />}
                  variant={uiControlsState.showChannelControl ? "contained" : "text"}
                  sx={{
                    color: uiControlsState.showChannelControl ? 'white' : theme.palette.text.secondary,
                    backgroundColor: uiControlsState.showChannelControl ? theme.palette.primary.main : 'transparent',
                    '&:hover': {
                      backgroundColor: uiControlsState.showChannelControl ? theme.palette.primary.dark : theme.palette.background.paper,
                      color: uiControlsState.showChannelControl ? 'white' : theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {t('Stream')}
                </Button>
              </Tooltip>
              <Tooltip title={t('Room Settings - Configure video, audio, and device settings')} arrow>
                <Button
                  onClick={onToggleSettings}
                  startIcon={<Settings />}
                  variant={uiControlsState.showSettings ? "contained" : "text"}
                  sx={{
                    color: uiControlsState.showSettings ? 'white' : theme.palette.text.secondary,
                    backgroundColor: uiControlsState.showSettings ? theme.palette.primary.main : 'transparent',
                    '&:hover': {
                      backgroundColor: uiControlsState.showSettings ? theme.palette.primary.dark : theme.palette.background.paper,
                      color: uiControlsState.showSettings ? 'white' : theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {t('Settings')}
                </Button>
              </Tooltip>
              <Tooltip title={t('Live Comments - View and manage comments from all connected social networks')} arrow>
                <Button
                  onClick={onToggleComments}
                  startIcon={<Comment />}
                  variant={uiControlsState.showComments ? "contained" : "text"}
                  sx={{
                    color: uiControlsState.showComments ? 'white' : theme.palette.text.secondary,
                    backgroundColor: uiControlsState.showComments ? theme.palette.primary.main : 'transparent',
                    '&:hover': {
                      backgroundColor: uiControlsState.showComments ? theme.palette.primary.dark : theme.palette.background.paper,
                      color: uiControlsState.showComments ? 'white' : theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {t('Comments')}
                </Button>
              </Tooltip>
              <Tooltip title={t('Leave Room - Disconnect from the live studio session')} arrow>
                <Button
                  onClick={onLeaveRoom}
                  startIcon={<ExitToApp />}
                  variant="text"
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.error.main + '20',
                      color: theme.palette.error.main,
                    },
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                    marginLeft: 0.5,
                  }}
                >
                  {t('Leave')}
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
